import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShieldHalf, LogOut, Loader2, Inbox, Search, Download, Send, Flame, BarChart3, Wifi } from "lucide-react";
import { Seo } from "../components/Seo";
import { AnalyticsPanel } from "../components/admin/AnalyticsPanel";
import { InquiriesTable } from "../components/admin/InquiriesTable";
import { InquiryDetailModal } from "../components/admin/InquiryDetailModal";
import { TIER_ORDER } from "../components/admin/badges";
import { downloadCsv } from "../components/admin/csv";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const matchesFilters = (i, { filter, serviceFilter, query, dateFrom, dateTo }) => {
  if (filter === "hot") {
    if (i.lead_tier !== "hot") return false;
  } else if (filter === "connectivity") {
    if (i.inquiry_type !== "connectivity") return false;
  } else if (filter !== "all" && (i.status || "new") !== filter) return false;
  if (serviceFilter !== "all" && !(i.services || []).includes(serviceFilter)) return false;
  if (query) {
    const hay = [i.org_name, i.full_name, i.email, i.industry, i.hq_city_state, ...(i.services || [])].join(" ").toLowerCase();
    if (!hay.includes(query.toLowerCase())) return false;
  }
  const created = new Date(i.created_at);
  if (dateFrom && created < new Date(dateFrom)) return false;
  if (dateTo && created > new Date(dateTo + "T23:59:59")) return false;
  return true;
};

const byTierThenDate = (a, b) => {
  const t = (TIER_ORDER[a.lead_tier] ?? 2) - (TIER_ORDER[b.lead_tier] ?? 2);
  if (t !== 0) return t;
  return new Date(b.created_at) - new Date(a.created_at);
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [authState, setAuthState] = useState(location.state?.user ? true : null);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [sendingBackup, setSendingBackup] = useState(false);

  const sendBackup = async () => {
    setSendingBackup(true);
    try {
      const res = await axios.post(`${API}/backup/send`, {}, { withCredentials: true });
      toast.success(`Backup CSV emailed (${res.data.inquiries_in_backup} inquiries attached).`);
    } catch { toast.error("Failed to send backup."); }
    finally { setSendingBackup(false); }
  };
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    if (location.state?.user?.is_admin) return;
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => {
        if (!res.data.is_admin) {
          toast.error("This Google account is not authorized for admin access.");
          navigate("/admin/login", { replace: true });
          return;
        }
        setUser(res.data);
        setAuthState(true);
      })
      .catch(() => navigate("/admin/login", { replace: true }));
  }, [location.state, navigate]);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/inquiries`, { withCredentials: true })
      .then((res) => setInquiries(res.data))
      .catch(() => toast.error("Failed to load inquiries."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (authState) load(); }, [authState, load]);

  const updateInquiryNotes = (id, notes) => {
    setSelected((s) => (s && s.id === id ? { ...s, notes } : s));
    setInquiries((list) => list.map((i) => (i.id === id ? { ...i, notes } : i)));
  };

  const updatePaymentSummary = (id, payments) => {
    const paid = payments.filter((p) => p.payment_status === "paid");
    const summary = {
      paid_count: paid.length,
      paid_total: paid.reduce((sum, p) => sum + Number(p.amount || 0), 0),
      pending_payments: payments.filter((p) => p.payment_status === "pending").length,
    };
    setSelected((s) => (s && s.id === id ? { ...s, ...summary } : s));
    setInquiries((list) => list.map((i) => (i.id === id ? { ...i, ...summary } : i)));
  };

  const addNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    setSavingNote(true);
    try {
      const res = await axios.post(`${API}/inquiries/${selected.id}/notes`, { text }, { withCredentials: true });
      updateInquiryNotes(selected.id, [...(selected.notes || []), res.data]);
      setNoteText("");
      toast.success("Note added.");
    } catch { toast.error("Failed to add note."); }
    finally { setSavingNote(false); }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/inquiries/${selected.id}/notes/${noteId}`, { withCredentials: true });
      updateInquiryNotes(selected.id, (selected.notes || []).filter((n) => n.id !== noteId));
      toast.success("Note deleted.");
    } catch { toast.error("Failed to delete note."); }
  };

  const sendReply = async (subject, message) => {
    try {
      const res = await axios.post(`${API}/inquiries/${selected.id}/reply`, { subject, message }, { withCredentials: true });
      updateInquiryNotes(selected.id, [...(selected.notes || []), res.data]);
      toast.success(`Reply sent to ${selected.email}.`);
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reply.");
      return false;
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/inquiries/${id}/status`, { status }, { withCredentials: true });
      setInquiries((list) => list.map((i) => (i.id === id ? { ...i, status } : i)));
      setSelected((s) => (s && s.id === id ? { ...s, status } : s));
      toast.success(`Marked as ${status}.`);
    } catch { toast.error("Update failed."); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/inquiries/${id}`, { withCredentials: true });
      setInquiries((list) => list.filter((i) => i.id !== id));
      setSelected(null);
      toast.success("Inquiry deleted.");
    } catch { toast.error("Delete failed."); }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const res = await axios.post(`${API}/digest/send`, {}, { withCredentials: true });
      toast.success(`Monthly digest sent (${res.data.inquiries_in_digest} inquiries from the past 30 days).`);
    } catch { toast.error("Failed to send digest."); }
    finally { setSendingDigest(false); }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    navigate("/admin/login", { replace: true });
  };

  if (authState === null) {
    return <div className="bg-[#05071F] min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" /></div>;
  }

  const counts = { all: inquiries.length, new: 0, reviewed: 0, archived: 0 };
  inquiries.forEach((i) => { counts[i.status || "new"] = (counts[i.status || "new"] || 0) + 1; });
  const hotCount = inquiries.filter((i) => i.lead_tier === "hot").length;
  const connectivityCount = inquiries.filter((i) => i.inquiry_type === "connectivity").length;
  const allServices = [...new Set(inquiries.flatMap((i) => i.services || []))].sort();
  const visible = inquiries
    .filter((i) => matchesFilters(i, { filter, serviceFilter, query, dateFrom, dateTo }))
    .sort(byTierThenDate);

  const exportCsv = () => {
    if (visible.length === 0) { toast.error("Nothing to export in the current view."); return; }
    downloadCsv(visible);
    toast.success(`Exported ${visible.length} inquiries to CSV.`);
  };

  return (
    <div data-testid="admin-dashboard" className="bg-[#05071F] min-h-screen text-white font-body">
      <Seo title="Inquiry Dashboard | Silent Sentry Admin" description="Restricted admin area." noindex />
      <header className="border-b border-white/10 bg-[#0B1035]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldHalf className="w-6 h-6 text-cyan-400" />
            <span className="font-heading font-semibold tracking-tight">SILENT SENTRY · Admin</span>
          </div>
          <div className="flex items-center gap-4">
            {user && <span data-testid="admin-user-email" className="text-sm text-slate-400 hidden sm:block">{user.email}</span>}
            <button onClick={logout} data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 text-sm border border-white/20 px-4 py-2 rounded-full hover:border-white/50 transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-3xl tracking-tight font-medium">Inquiry Dashboard</h1>
            <p className="mt-2 text-sm text-slate-400">
              Every intake form submission, newest first. Alerts are emailed to janthony@silentsentrycorp.com ·
              <span className="text-violet-300"> monthly digest + CSV backup on the 1st, 8:00 AM Central</span>.
            </p>
          </div>
          <button onClick={() => setShowAnalytics((v) => !v)} data-testid="toggle-analytics-btn"
            className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border transition-colors ${
              showAnalytics ? "bg-cyan-400/15 text-cyan-300 border-cyan-400/40" : "border-white/20 text-slate-300 hover:border-white/50"
            }`}>
            <BarChart3 className="w-4 h-4" /> {showAnalytics ? "Hide Analytics" : "Show Analytics"}
          </button>
        </div>

        {showAnalytics && <AnalyticsPanel inquiries={inquiries} />}

        <div className="mt-8 flex flex-wrap items-center gap-2">
          {["all", "new", "reviewed", "archived"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`text-sm px-4 py-2 rounded-full border transition-colors capitalize ${
                filter === f ? "bg-cyan-400 text-[#05071F] border-cyan-400 font-semibold" : "border-white/20 text-slate-300 hover:border-white/50"
              }`}>
              {f} ({counts[f] || 0})
            </button>
          ))}
          <button onClick={() => setFilter("hot")} data-testid="filter-hot"
            className={`inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border transition-colors ${
              filter === "hot" ? "bg-orange-400 text-[#05071F] border-orange-400 font-semibold" : "border-orange-400/40 text-orange-300 hover:border-orange-400"
            }`}>
            <Flame className="w-4 h-4" /> Hot ({hotCount})
          </button>
          <button onClick={() => setFilter("connectivity")} data-testid="filter-connectivity"
            className={`inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border transition-colors ${
              filter === "connectivity" ? "bg-violet-400 text-[#05071F] border-violet-400 font-semibold" : "border-violet-400/40 text-violet-300 hover:border-violet-400"
            }`}>
            <Wifi className="w-4 h-4" /> Connectivity ({connectivityCount})
          </button>
          <button onClick={sendDigest} disabled={sendingDigest} data-testid="send-digest-btn"
            className="ml-auto inline-flex items-center gap-2 text-sm border border-violet-400/40 text-violet-300 px-5 py-2 rounded-full hover:bg-violet-400/10 transition-colors disabled:opacity-60">
            {sendingDigest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Digest Now
          </button>
          <button onClick={sendBackup} disabled={sendingBackup} data-testid="send-backup-btn"
            className="inline-flex items-center gap-2 text-sm border border-violet-400/40 text-violet-300 px-5 py-2 rounded-full hover:bg-violet-400/10 transition-colors disabled:opacity-60">
            {sendingBackup ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Email Backup
          </button>
          <button onClick={exportCsv} data-testid="export-csv-btn"
            className="inline-flex items-center gap-2 text-sm bg-violet-500 text-white font-semibold px-5 py-2 rounded-full hover:bg-violet-400 transition-colors">
            <Download className="w-4 h-4" /> Export CSV ({visible.length})
          </button>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="search-input" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search org, contact, email…"
              className="w-full bg-[#0B1035] border border-white/15 rounded-full pl-9 pr-4 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-violet-400" />
          </div>
          <select data-testid="service-filter" value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}
            className="bg-[#0B1035] border border-white/15 rounded-full px-4 py-2 text-sm text-white focus:outline-none focus:border-violet-400">
            <option value="all">All services</option>
            {allServices.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 uppercase tracking-widest">From</span>
            <input data-testid="date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="flex-1 bg-[#0B1035] border border-white/15 rounded-full px-4 py-2 text-sm text-white focus:outline-none focus:border-violet-400 [color-scheme:dark]" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 uppercase tracking-widest">To</span>
            <input data-testid="date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="flex-1 bg-[#0B1035] border border-white/15 rounded-full px-4 py-2 text-sm text-white focus:outline-none focus:border-violet-400 [color-scheme:dark]" />
          </div>
        </div>

        {loading ? (
          <div className="mt-16 flex justify-center"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" /></div>
        ) : visible.length === 0 ? (
          <div data-testid="empty-state" className="mt-16 text-center text-slate-500">
            <Inbox className="w-10 h-10 mx-auto mb-4" /> No inquiries in this view.
          </div>
        ) : (
          <InquiriesTable inquiries={visible} onSelect={setSelected} />
        )}
      </main>

      {selected && (
        <InquiryDetailModal
          inquiry={selected}
          onClose={() => setSelected(null)}
          onUpdateStatus={updateStatus}
          onDelete={remove}
          onSendReply={sendReply}
          onPaymentsChange={(payments) => updatePaymentSummary(selected.id, payments)}
          onNoteAdded={(note) => updateInquiryNotes(selected.id, [...(selected.notes || []), note])}
          noteText={noteText}
          onNoteTextChange={setNoteText}
          savingNote={savingNote}
          onAddNote={addNote}
          onDeleteNote={deleteNote}
        />
      )}
    </div>
  );
}
